<script>
  import { createEventDispatcher } from 'svelte';
  import {
    customFieldOptionValue,
    staleCustomFieldOption
  } from '../../lib/settings/jobCustomFields.js';
  import { validateSlots } from '../../lib/trackingSlots.js';

  export let slots = [];
  export let jobCustomFields = [];
  export let loadingCustomFields = false;
  export let customFieldsError = null;
  export let categories = [];
  export let loadingCategories = false;
  export let categoriesError = null;
  export let idPrefix = 'tracking';

  const dispatch = createEventDispatcher();

  $: activeCategories = (categories || []).filter((category) => !category.status || category.status === 'ACTIVE');
  $: conflict = validateSlots(slots);

  function patch(index, changes, disable = false) {
    const next = slots.map((slot, i) => {
      if (i !== index) return slot;
      return { ...slot, ...changes, ...(disable ? { enabled: false } : {}) };
    });
    dispatch('change', next);
  }

  function onField(index, event) {
    patch(index, { jtField: event.target.value }, true);
  }

  function onCategory(index, event) {
    const id = event.target.value;
    const category = activeCategories.find((entry) => entry.trackingCategoryID === id);
    patch(index, {
      xeroCategoryID: id,
      xeroCategoryName: category?.name || ''
    }, true);
  }

  function onToggle(index, event) {
    patch(index, { enabled: event.target.checked });
    dispatch('save');
  }

  function otherCategoryId(index) {
    const other = slots[index === 0 ? 1 : 0];
    return other?.xeroCategoryID || '';
  }

  function otherField(index) {
    const other = slots[index === 0 ? 1 : 0];
    return other?.enabled ? (other.jtField || '') : '';
  }

  function selectedCategory(slot) {
    return activeCategories.find((entry) => entry.trackingCategoryID === slot.xeroCategoryID) || null;
  }

  function slotCanEnable(slot, index) {
    if (!(slot.jtField && selectedCategory(slot))) return false;
    const other = slots[index === 0 ? 1 : 0];
    if (!other?.enabled) return true;
    if (other.jtField && other.jtField === slot.jtField) return false;
    if (other.xeroCategoryID && other.xeroCategoryID === slot.xeroCategoryID) return false;
    return true;
  }
</script>

{#each slots as trackingSlot, index (trackingSlot.slot)}
  {@const staleField = staleCustomFieldOption(trackingSlot.jtField, jobCustomFields)}
  {@const selected = selectedCategory(trackingSlot)}
  {@const missingCategory = trackingSlot.xeroCategoryID && !selected}
  <div class="border border-gray-200 rounded-lg p-4 bg-white space-y-4">
    <h5 class="text-sm font-semibold text-gray-900">Tracking category {trackingSlot.slot}</h5>

    <div>
      <label for="{idPrefix}-field-{trackingSlot.slot}" class="block text-sm font-medium text-gray-700 mb-2">JobTread field</label>
      <select
        id="{idPrefix}-field-{trackingSlot.slot}"
        value={trackingSlot.jtField}
        on:change={(event) => onField(index, event)}
        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
      >
        <option value="">-- Select field --</option>
        <option value="Number" disabled={otherField(index) === 'Number'}>Number</option>
        <option value="Name" disabled={otherField(index) === 'Name'}>Name</option>
        <option value="Both" disabled={otherField(index) === 'Both'}>Name - Number</option>
        {#if jobCustomFields.length > 0 || staleField}
          <optgroup label="Job custom fields">
            {#if staleField}
              <option value={customFieldOptionValue(staleField.id)}>{staleField.name}</option>
            {/if}
            {#each jobCustomFields as cf (cf.id)}
              <option value={customFieldOptionValue(cf.id)} disabled={otherField(index) === customFieldOptionValue(cf.id)}>{cf.name}</option>
            {/each}
          </optgroup>
        {/if}
      </select>
      <p class="mt-1 text-xs text-gray-500">
        Written to this Xero category on each line. A blank custom field uses the job name.
      </p>
      {#if loadingCustomFields}
        <p class="mt-1 text-xs text-gray-400">Loading custom fields…</p>
      {:else if customFieldsError}
        <p class="mt-1 text-xs text-amber-600">Could not load custom fields: {customFieldsError}</p>
      {/if}
    </div>

    <div>
      <label for="{idPrefix}-category-{trackingSlot.slot}" class="block text-sm font-medium text-gray-700 mb-2">Xero tracking category</label>
      <select
        id="{idPrefix}-category-{trackingSlot.slot}"
        value={trackingSlot.xeroCategoryID}
        on:change={(event) => onCategory(index, event)}
        on:focus={() => dispatch('loadCategories')}
        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
      >
        <option value="">-- Select category --</option>
        {#if missingCategory}
          <option value={trackingSlot.xeroCategoryID}>{trackingSlot.xeroCategoryName || 'Saved category'} (not active)</option>
        {/if}
        {#if loadingCategories}
          <option disabled>Loading…</option>
        {:else}
          {#each activeCategories as category (category.trackingCategoryID)}
            <option
              value={category.trackingCategoryID}
              disabled={otherCategoryId(index) === category.trackingCategoryID}
            >
              {category.name}
            </option>
          {/each}
        {/if}
      </select>
      {#if selected}
        <p class="mt-1 text-xs text-gray-500">{selected.optionCount ?? 0} of 100 options used</p>
      {:else if categoriesError}
        <p class="mt-1 text-xs text-amber-600">Could not load Xero categories: {categoriesError}</p>
      {:else}
        <p class="mt-1 text-xs text-gray-500">Active categories from your Xero organisation. Inactive categories are not listed.</p>
      {/if}
    </div>

    <div class="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-md">
      <div>
        <label for="{idPrefix}-enabled-{trackingSlot.slot}" class="block text-sm font-medium text-gray-700">
          Use tracking category {trackingSlot.slot}
        </label>
        <span class="block text-xs text-gray-500 mt-1">
          {#if !slotCanEnable(trackingSlot, index)}
            Choose a JobTread field and an active Xero category to turn this on
          {:else}
            {trackingSlot.jtField.startsWith('cf:') ? 'Custom field' : trackingSlot.jtField} → {trackingSlot.xeroCategoryName}
          {/if}
        </span>
      </div>
      <label class="relative inline-flex items-center cursor-pointer">
        <input
          id="{idPrefix}-enabled-{trackingSlot.slot}"
          type="checkbox"
          checked={trackingSlot.enabled}
          on:change={(event) => onToggle(index, event)}
          disabled={!slotCanEnable(trackingSlot, index) && !trackingSlot.enabled}
          class="sr-only peer"
        />
        <div class="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
      </label>
    </div>
  </div>
{/each}

{#if conflict}
  <div class="p-3 bg-red-50 border border-red-300 rounded-md">
    <p class="text-sm text-red-800">{conflict}</p>
  </div>
{/if}
